import React from "react";
import { useQuery } from "react-query";
import api from "../lib/api";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { useAuth } from "@/contexts/AuthContext";
import {
  BarChart3,
  TrendingUp,
  AlertTriangle,
  Users,
  FileText,
  Plus,
  ArrowRight,
  Activity,
} from "lucide-react";
import { Link } from "react-router-dom";
import FieldEngineerReports from "./FieldEngineerReports";
import PeerReviewerReports from "./PeerReviewerReports";
import SiteReviewerReports from "./SiteReviewerReports";
import DirectorReviewerReports from "./DirectorReviewerReports";
import UserPlusIcon from "@/components/icons/UserPlusIcon";
import UserIcon from "@/components/icons/UserIcon";
import UsersIcon from "@/components/icons/UsersIcon";
import FileIcon from "@/components/icons/FileIcon";
import LocationIcon from "@/components/icons/LocationIcon";

const DashboardPage = () => {
  const { user, site, logout, isAdmin } = useAuth();

  const { data: statsData, isLoading: statsLoading } = useQuery(
    ["dashboard-stats"],
    async () => {
      const res = await api.get("/dashboard/stats");
      return res.data.data;
    },
    {
      enabled: !!user,
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    }
  );

  const StatCard = ({
    title,
    value,
    subtitle,
    icon: Icon,
    color = "primary",
  }) => (
    <div
      className={`card-hover h-full rounded-lg bg-navy-100 p-6 shadow-lg backdrop-blur-sm slide-up`}
    >
      <div className="flex items-center justify-between">
        <div className={``}>
          <Icon className="w-8 h-8 p-0 mb-2 text-red-500" />
        </div>
      </div>
      <div>
        <div className="mb-1 text-2xl font-bold text-gray-0">
          {statsLoading ? <div className="h-8 rounded animate-pulse" /> : value}
        </div>
        <div className="text-sm text-gray-400">{title}</div>
        <div className="mt-2 min-h-4 text-xs text-gray-500">
          {statsLoading ? (
            <div className="w-32 h-4 rounded animate-pulse" />
          ) : (
            subtitle || "\u00A0"
          )}
        </div>
      </div>
    </div>
  );

  const getRiskBadgeVariant = (severity) => {
    switch (severity?.toLowerCase()) {
      case "high":
        return "destructive";
      case "medium":
        return "warning";
      case "low":
        return "success";
      default:
        return "secondary";
    }
  };

  return (
    <div className="">
      {isAdmin ? (
        <div className="">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2 lg:grid-cols-4">
            <Link to="/users" className="block h-full">
              <StatCard
                title="Users"
                value={statsData ? statsData.users.total : "-"}
                subtitle={
                  statsData
                    ? `Active: ${statsData.users.active} • Inactive: ${statsData.users.inactive}`
                    : undefined
                }
                icon={UserIcon}
                color="accent"
                delay={0}
              />
            </Link>
            <Link to="/clients" className="block h-full">
              <StatCard
                title="Clients"
                value={statsData ? statsData.clients.total : "-"}
                subtitle={
                  statsData
                    ? `Active: ${statsData.clients.active} • Inactive: ${statsData.clients.inactive}`
                    : undefined
                }
                icon={UsersIcon}
                color="primary"
                delay={1}
              />
            </Link>
            <Link to="/site" className="block h-full">
              <StatCard
                title="Sites"
                value={statsData ? statsData.sites.total : "-"}
                // subtitle={
                //   statsData
                //     ? `Active: ${statsData.sites.active} • Inactive: ${statsData.sites.inactive}`
                //     : undefined
                // }
                icon={LocationIcon}
                color="accent"
                delay={2}
              />
            </Link>
            <Link to="/reports" className="block h-full">
              <StatCard
                title="Reports"
                value={statsData ? statsData.reports.total : "-"}
                // subtitle={
                //   statsData
                //     ? `Active: ${statsData.reports.active} • Inactive: ${statsData.reports.inactive}`
                //     : undefined
                // }
                icon={FileIcon}
                color="primary"
                delay={3}
              />
            </Link>
          </div>
        </div>
      ) : user.roles?.some((role) => ["field-engineer", "Field Engineer"].includes(role)) ? (
        <FieldEngineerReports />
      ) : user.roles?.some((role) => ["peer-reviewer", "Peer Reviewer"].includes(role)) ? (
        <PeerReviewerReports />
      ) : user.roles?.some((role) => ["site-reviewer", "Site Reviewer"].includes(role)) ? (
        <SiteReviewerReports />
      ) : user.roles?.some((role) => ["director-reviewer", "Director Reviewer"].includes(role)) ? (
        <DirectorReviewerReports />
      ) : (
        <div className="text-center text-gray-400">No dashboard available for your role.</div>
      )
      }
    </div >
  );
};

export default DashboardPage;
